import type { Child } from "zyra";
import { CodeBlock } from "./CodeBlock";
import { headingId } from "../lib/headings";
import type { SiteState } from "../app/site-state";
export function Markdown(props: { site: SiteState; body: string }) {
  const { router } = props.site;
  function inline(text: string): Child[] {
    return text
      .split(/(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g)
      .map((part) => {
        if (part.startsWith("`"))
          return <code class="inline-code">{part.slice(1, -1)}</code>;
        if (part.startsWith("**")) return <strong>{part.slice(2, -2)}</strong>;
        const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
        if (!link) return part;
        const [, label, target] = link;
        const match =
          /(?:^|\/)(DEPLOYMENT|SECURITY|VALIDATION|IMPLEMENTATION)\.md(?:#(.*))?$/i.exec(
            target,
          );
        if (match)
          return (
            <router.Link
              href={
                "/docs/" +
                match[1].toLowerCase() +
                (match[2] ? "#" + match[2] : "")
              }
            >
              {label}
            </router.Link>
          );
        if (target.startsWith("/docs") || target.startsWith("#"))
          return <router.Link href={target}>{label}</router.Link>;
        if (/^https?:\/\//.test(target))
          return (
            <a href={target} target="_blank" rel="noopener noreferrer">
              {label} ↗
            </a>
          );
        return label;
      });
  }
  const lines = props.body.trim().split(/\r?\n/);
  const nodes: Child[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index++;
      continue;
    }
    if (line.startsWith("```")) {
      const language = line.slice(3);
      const block: string[] = [];
      index++;
      while (index < lines.length && !lines[index].startsWith("```"))
        block.push(lines[index++]);
      index++;
      nodes.push(<CodeBlock text={block.join("\n")} language={language} />);
      continue;
    }
    if (/^#{1,4} /.test(line)) {
      const title = line.replace(/^#+ /, "");
      nodes.push(
        <h2 id={headingId(title)}>
          <a class="heading-anchor" href={"#" + headingId(title)}>
            {inline(title)}
            <span aria-hidden="true">#</span>
          </a>
        </h2>,
      );
      index++;
      continue;
    }
    if (/^\s*[-*] /.test(line)) {
      const items: Child[] = [];
      while (index < lines.length && /^\s*[-*] /.test(lines[index]))
        items.push(<li>{inline(lines[index++].replace(/^\s*[-*] /, ""))}</li>);
      nodes.push(<ul>{items}</ul>);
      continue;
    }
    if (/^\d+\. /.test(line)) {
      const items: Child[] = [];
      while (index < lines.length && /^\d+\. /.test(lines[index]))
        items.push(<li>{inline(lines[index++].replace(/^\d+\. /, ""))}</li>);
      nodes.push(<ol>{items}</ol>);
      continue;
    }
    if (line.startsWith("|")) {
      const rows: string[][] = [];
      while (index < lines.length && lines[index].startsWith("|")) {
        const cells = lines[index++]
          .split("|")
          .slice(1, -1)
          .map((s) => s.trim());
        if (!cells.every((cell) => /^:?-+:?$/.test(cell))) rows.push(cells);
      }
      nodes.push(
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                {rows[0]?.map((cell) => (
                  <th scope="col">{inline(cell)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(1).map((row) => (
                <tr>
                  {row.map((cell) => (
                    <td>{inline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    const paragraph = [line];
    index++;
    while (
      index < lines.length &&
      lines[index].trim() &&
      !/^(#{1,4} |```|[-*] |\d+\. |\|)/.test(lines[index])
    )
      paragraph.push(lines[index++]);
    nodes.push(<p>{inline(paragraph.join(" "))}</p>);
  }
  return <div class="prose">{nodes}</div>;
}
